import bcrypt from 'bcryptjs';

import { logger } from '@/services/logger';
// パスワードハッシュ化設定
const SALT_ROUNDS = 10;
const MIN_PASSWORD_LENGTH = 8;

/**
 * パスワードのバリデーション
 */
export const validatePassword = (password: string): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (password.length < MIN_PASSWORD_LENGTH) {
    errors.push(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long`);
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (!/[!@#$%^&*()_+\-={};':"\\|,.<>/?]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * パスワードをハッシュ化
 */
export const hashPassword = async (password: string): Promise<string> => {
  return bcrypt.hash(password, SALT_ROUNDS);
};

/**
 * パスワードの検証 - 複数のハッシュ形式をサポート
 */
export const verifyPassword = async (password: string, hash: string): Promise<boolean> => {
  try {
    // bcryptハッシュの場合（従来の形式）
    if (hash.startsWith('$2a$') || hash.startsWith('$2b$') || hash.startsWith('$2y$')) {
      return await bcrypt.compare(password, hash);
    }
    
    // pbkdf2ハッシュの場合（パスワードリセット後）
    if (hash.startsWith('pbkdf2:')) {
      return await verifyPbkdf2Password(password, hash);
    }
    
    // webcryptoハッシュの場合（パスワードリセット後のフォールバック）
    if (hash.startsWith('webcrypto:')) {
      return await verifyWebCryptoPassword(password, hash);
    }
    
    // 不明な形式の場合はbcryptとして試行
    return await bcrypt.compare(password, hash);
  } catch (error) {
    logger.error('verifyPassword: Error during comparison:', error);
    return false;
  }
};

/**
 * pbkdf2ハッシュの検証
 */
const verifyPbkdf2Password = async (password: string, hash: string): Promise<boolean> => {
  try {
    const parts = hash.split(':');
    if (parts.length !== 3 || parts[0] !== 'pbkdf2') {
      return false;
    }
    
    const saltHex = parts[1];
    const storedHash = parts[2];
    
    // ブラウザ環境ではWebCrypto APIを使用
    const encoder = new TextEncoder();
    const salt = new Uint8Array(
      saltHex.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16))
    );
    
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    );
    
    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 10000,
        hash: 'SHA-512'
      },
      keyMaterial,
      512 // 64 bytes = 512 bits
    );
    
    const derivedArray = Array.from(new Uint8Array(derivedBits));
    const derivedHex = derivedArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return derivedHex === storedHash;
  } catch (error) {
    logger.error('verifyPbkdf2Password: Error during verification:', error);
    return false;
  }
};

/**
 * WebCrypto APIハッシュの検証
 */
const verifyWebCryptoPassword = async (password: string, hash: string): Promise<boolean> => {
  try {
    const parts = hash.split(':');
    if (parts.length !== 3 || parts[0] !== 'webcrypto') {
      return false;
    }
    
    const saltHex = parts[1];
    const storedHash = parts[2];
    
    const encoder = new TextEncoder();
    const salt = new Uint8Array(
      saltHex.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16))
    );
    
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    );
    
    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 10000,
        hash: 'SHA-256'
      },
      keyMaterial,
      256
    );
    
    const derivedArray = Array.from(new Uint8Array(derivedBits));
    const derivedHex = derivedArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return derivedHex === storedHash;
  } catch (error) {
    logger.error('verifyWebCryptoPassword: Error during verification:', error);
    return false;
  }
};

/**
 * ランダムな仮パスワードを生成
 */
export const generateTemporaryPassword = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';
  
  // 各種類の文字を最低1つ含む
  password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
  password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
  password += '0123456789'[Math.floor(Math.random() * 10)];
  password += '!@#$%^&*'[Math.floor(Math.random() * 8)];
  
  // 残りの文字をランダムに追加
  for (let i = password.length; i < 12; i++) {
    password += chars[Math.floor(Math.random() * chars.length)];
  }
  
  // シャッフル
  return password.split('').sort(() => Math.random() - 0.5).join('');
};